import { Country, State, City } from "country-state-city";

// Define country-specific address formats
export const ADDRESS_FORMATS = {
  US: {
    fields: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      state: "State",
      postalCode: "ZIP Code",
    },
    required: ["addressLine1", "city", "state", "postalCode"],
    hasStates: true,
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    postalCodePlaceholder: "12345 or 12345-6789",
  },
  CA: {
    fields: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      state: "Province",
      postalCode: "Postal Code",
    },
    required: ["addressLine1", "city", "state", "postalCode"],
    hasStates: true,
    postalCodePattern: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
    postalCodePlaceholder: "A1A 1A1",
  },
  GB: {
    fields: ["addressLine1", "addressLine2", "city", "county", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      county: "County",
      postalCode: "Postcode",
    },
    required: ["addressLine1", "city", "postalCode"],
    hasStates: false,
    postalCodePattern: /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/,
    postalCodePlaceholder: "SW1A 1AA",
  },
  AU: {
    fields: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      state: "State",
      postalCode: "Postcode",
    },
    required: ["addressLine1", "city", "state", "postalCode"],
    hasStates: true,
    postalCodePattern: /^\d{4}$/,
    postalCodePlaceholder: "1234",
  },
  IN: {
    fields: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      state: "State",
      postalCode: "PIN Code",
    },
    required: ["addressLine1", "city", "state", "postalCode"],
    hasStates: true,
    postalCodePattern: /^\d{6}$/,
    postalCodePlaceholder: "123456",
  },
  DE: {
    fields: ["addressLine1", "addressLine2", "city", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      postalCode: "Postleitzahl",
    },
    required: ["addressLine1", "city", "postalCode"],
    hasStates: false,
    postalCodePattern: /^\d{5}$/,
    postalCodePlaceholder: "12345",
  },
  FR: {
    fields: ["addressLine1", "addressLine2", "city", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      postalCode: "Code Postal",
    },
    required: ["addressLine1", "city", "postalCode"],
    hasStates: false,
    postalCodePattern: /^\d{5}$/,
    postalCodePlaceholder: "12345",
  },
  // Default format for countries not specifically defined
  DEFAULT: {
    fields: ["addressLine1", "addressLine2", "city", "state", "postalCode"],
    labels: {
      addressLine1: "Address Line 1",
      addressLine2: "Address Line 2 (optional)",
      city: "City",
      state: "State/Region",
      postalCode: "Postal Code",
    },
    required: ["addressLine1", "city", "postalCode"],
    hasStates: true,
    postalCodePattern: /^.+$/,
    postalCodePlaceholder: "Enter postal code",
  },
};

// Get address format for a specific country
export const getAddressFormat = (countryCode) => {
  return ADDRESS_FORMATS[countryCode] || ADDRESS_FORMATS.DEFAULT;
};

// Get countries for dropdown (enhanced with iso code)
export const getCountriesForSelect = () => {
  return Country.getAllCountries().map((country) => ({
    value: country.isoCode,
    label: country.name,
    flag: country.flag,
  }));
};

// Get states/provinces for a specific country
export const getStatesForCountry = (countryCode) => {
  if (!countryCode) return [];

  const states = State.getStatesOfCountry(countryCode);
  return states.map((state) => ({
    value: state.isoCode,
    label: state.name,
  }));
};

// Get cities for a specific country and state
export const getCitiesForCountryAndState = (countryCode, stateCode) => {
  if (!countryCode) return [];

  const cities = stateCode
    ? City.getCitiesOfState(countryCode, stateCode)
    : City.getCitiesOfCountry(countryCode);

  return cities.map((city) => ({
    value: city.name,
    label: city.name,
  }));
};

// Validate postal code for a specific country
export const validatePostalCode = (countryCode, postalCode) => {
  if (!postalCode) return false;

  const format = getAddressFormat(countryCode);
  return format.postalCodePattern.test(postalCode);
};

// Get postal code error message
export const getPostalCodeError = (countryCode) => {
  const format = getAddressFormat(countryCode);
  return `Please enter a valid ${format.labels.postalCode.toLowerCase()} (e.g., ${format.postalCodePlaceholder})`;
};

// Check if a field is required for a specific country
export const isFieldRequired = (countryCode, fieldName) => {
  const format = getAddressFormat(countryCode);
  return format.required.includes(fieldName);
};

// Get field label for a specific country
export const getFieldLabel = (countryCode, fieldName) => {
  const format = getAddressFormat(countryCode);
  return format.labels[fieldName] || fieldName;
};

// Check if country has states/provinces
export const countryHasStates = (countryCode) => {
  const format = getAddressFormat(countryCode);
  return format.hasStates;
};
